import type { Au08Output, LhcResult, MlsResult } from "./types.js";
import type { HospitalTier } from "./hospital-premiums-types.js";

/** Whole-dollar AUD without the leading "A$" — for inline use mid-sentence
 * where the surrounding text already establishes currency. */
function aud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

// Plain-English result copy (spec §5.2). Lives in the rules package — not in any
// single app — so every surface that explains an AU-08 result (the Expo result
// screen, the "my results" list, the PDF report) renders identical wording from
// one source. That matters for regulation, not just DRY: every string here is
// information, not advice (validation §3.1) — we state what the user currently
// pays / would pay and why, sourced from the engine's own output, with no "save
// $X", no "switch", no product recommendation. Drift between channels is the
// information → advice kill-threat, so the copy is centralised here alongside
// the rules it describes. Pure string-builders, unit-tested with the engine.

const FY_LABEL = "FY26";

/** Whole-dollar AUD with thousands separators. Kept local to the copy layer so
 * the wording renders the same numbers everywhere, independent of any app's UI
 * formatter. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** Loading is always a whole-percent multiple (2%/yr); rate can be 1.25% etc. */
function loadingPct(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

function ratePct(fraction: number): string {
  return `${+(fraction * 100).toFixed(2)}%`;
}

export function mlsHeadline(mls: MlsResult): string {
  return mls.amount === 0
    ? "A$0 — you don't pay the Medicare Levy Surcharge"
    : `Your MLS for ${FY_LABEL} (estimate): ${formatAud(mls.amount)}`;
}

export function lhcHeadline(lhc: LhcResult): string {
  switch (lhc.status) {
    case "not_yet_applicable":
    case "no_loading":
      return "No Lifetime Health Cover loading";
    case "would_apply_if_taken_now":
      return `Lifetime Health Cover loading if you took cover now: ${loadingPct(lhc.loading)}`;
    case "applies": {
      const yrs = lhc.yearsRemaining;
      const tail =
        yrs !== null ? ` (${yrs} ${yrs === 1 ? "year" : "years"} remaining)` : "";
      return `Your Lifetime Health Cover loading: ${loadingPct(lhc.loading)}${tail}`;
    }
    case "indeterminate":
      return "Lifetime Health Cover loading: not enough information";
  }
}

/** The one concrete annual dollar figure is the MLS; LHC is a percentage
 * loading on a premium we don't know, so we never invent a combined total. */
export function annualExposure(mls: MlsResult): string {
  return mls.amount === 0
    ? "A$0 in Medicare Levy Surcharge this year"
    : `${formatAud(mls.amount)} in Medicare Levy Surcharge this year`;
}

export function mlsBullet(mls: MlsResult): string {
  if (mls.exemptViaCover) {
    return `You hold private hospital cover, so the Medicare Levy Surcharge doesn't apply to you for ${FY_LABEL}.`;
  }
  if (mls.amount === 0) {
    return `Your income is below the ${FY_LABEL} Medicare Levy Surcharge threshold, so no surcharge applies.`;
  }
  return `Because you don't hold private hospital cover and your income sits in the ${ratePct(mls.rate)} surcharge tier, the Medicare Levy Surcharge applies at ${ratePct(mls.rate)} of your income (${formatAud(mls.leviedIncome)}).`;
}

export function lhcBullet(lhc: LhcResult): string {
  switch (lhc.status) {
    case "not_yet_applicable":
      return "Lifetime Health Cover loading only starts after 1 July following your 31st birthday, so it doesn't apply at your age yet.";
    case "no_loading":
      return "You have no Lifetime Health Cover loading — either you held hospital cover by the base date following your 31st birthday, or a past loading has already dropped off after 10 continuous years of cover.";
    case "would_apply_if_taken_now":
      return `You don't hold hospital cover. If you took out cover now, a ${loadingPct(lhc.loading)} loading would be added to your premium, because cover wasn't held by the base date following your 31st birthday. The loading drops off after 10 continuous years of cover.`;
    case "applies": {
      const yrs = lhc.yearsRemaining;
      const tail =
        yrs !== null ? ` — ${yrs} ${yrs === 1 ? "year" : "years"} to go` : "";
      return `Your premium currently carries a ${loadingPct(lhc.loading)} Lifetime Health Cover loading because cover was first held after the base date. It drops off after 10 continuous years of cover${tail}.`;
    }
    case "indeterminate":
      return "You hold hospital cover but didn't tell us when it started, so we can't work out your Lifetime Health Cover loading. Your insurer can confirm your LHC base date.";
  }
}

/**
 * Family-tier explanation: states that, for couples/families, the surcharge
 * *tier* is set by COMBINED income while the surcharge itself is levied on the
 * person's own income. Information only — describes the ATO mechanic, makes no
 * recommendation. Returns `null` for single filers / when the household isn't
 * on the family table. `childCount` lets us name the per-child threshold
 * effect when relevant.
 */
export function familyTierBullet(
  mls: MlsResult,
  childCount: number,
): string | null {
  if (mls.spouseCover === "single") return null;
  const combined = aud(mls.testedIncome);
  const extraChildren = Math.max(0, childCount - 1);
  // The increment only moves thresholds when there are 2+ children.
  const childClause =
    extraChildren > 0
      ? ` Your family income threshold is raised by ${aud(1_500)} for each dependent child after the first (${extraChildren} ${extraChildren === 1 ? "child" : "children"} here, so +${aud(extraChildren * 1_500)}), which can move you into a lower surcharge tier.`
      : "";
  if (mls.rate === 0) {
    return `For couples and families the Medicare Levy Surcharge tier is set by your combined income for surcharge purposes (${combined}), which is below the ${FY_LABEL} family threshold, so no surcharge applies.${childClause}`;
  }
  return `For couples and families the surcharge tier is set by your combined income for surcharge purposes (${combined}), which sits in the ${ratePct(mls.rate)} tier; the surcharge itself is then worked out on your own income (${aud(mls.leviedIncome)}), not the combined figure.${childClause}`;
}

/**
 * Spouse / household hospital-cover situation. Each person's MLS is assessed
 * individually, but the ATO only treats you as covered when an appropriate
 * hospital policy covers you, your spouse AND all dependants. We state the
 * factual household position — never "add your partner to a policy", never a
 * dollar saving. Returns `null` for single filers.
 */
export function spouseCoverBullet(mls: MlsResult): string | null {
  switch (mls.spouseCover) {
    case "single":
      return null;
    case "all_covered":
      return `You and your partner both hold private hospital cover, so the Medicare Levy Surcharge doesn't apply to either of you for ${FY_LABEL}.`;
    case "user_only":
      // User personally exempt, but the household isn't fully covered.
      return `You hold private hospital cover but, based on what you told us, your partner does not. The Medicare Levy Surcharge is assessed per person: it doesn't apply to you, but it can still apply to your partner on their own income. To remove the surcharge for the whole household, the ATO requires an appropriate hospital policy covering you, your partner and any dependants.`;
    case "partner_only":
      return `Your partner holds private hospital cover but, based on what you told us, you do not. The Medicare Levy Surcharge is assessed per person, so it can apply to you on your own income even though your partner is covered. The ATO treats a household as fully covered only when an appropriate hospital policy covers you, your partner and any dependants.`;
    case "none_covered":
      return `Based on what you told us, neither you nor your partner holds private hospital cover. The Medicare Levy Surcharge is assessed per person on each partner's own income, using the tier set by your combined income. The ATO treats a household as fully covered only when an appropriate hospital policy covers you, your partner and any dependants.`;
  }
}

/** Source-attribution line (3rd bullet), composed from the ruleset metadata
 * the server returns so it always names the rules actually in force. */
export function sourceBullet(ruleVersion: string, verifiedAt: string): string {
  return `These figures use the ATO and APRA rules for ${FY_LABEL} (ruleset ${ruleVersion}), last verified ${verifiedAt}. Rules and thresholds change each financial year — always check ato.gov.au and privatehealth.gov.au.`;
}

export function explanationBullets(
  output: Au08Output,
  ruleVersion: string,
  verifiedAt: string,
  /** Dependent-child count from the assessment input — lets the family
   * bullets name the per-child threshold effect. Defaults to 0 (no effect)
   * so existing single-filer callers need not pass it. */
  childCount = 0,
): string[] {
  // Single filers get the original three bullets; family/couple filers get
  // two extra information-only bullets (combined-income tier + household
  // cover situation) inserted before the source line.
  const family = familyTierBullet(output.mls, childCount);
  const spouse = spouseCoverBullet(output.mls);
  return [
    mlsBullet(output.mls),
    ...(family ? [family] : []),
    ...(spouse ? [spouse] : []),
    lhcBullet(output.lhc),
    sourceBullet(ruleVersion, verifiedAt),
  ];
}

/** Minimal view of the benchmark figure the copy layer needs — mirrors the
 * `benchmark` block the API returns. Kept here (not coupled to the API shape)
 * so every surface renders the "How you compare" strip identically. */
export interface Au08BenchmarkView {
  /** APRA age band the user falls in, e.g. "35-39". */
  ageBand: string;
  /** People in that band with private hospital cover (APRA, a count). */
  insuredPersons: number;
  /** Share of the band covered (0–1), or null when no population is sourced. */
  coverageRate: number | null;
  /** The user's own stated cover status. */
  holdsCover: boolean;
  /** Period the numerator (APRA counts) covers, e.g. "APRA March 2026 quarter". */
  period: string;
  /** Period the denominator (ABS ERP) covers; cited when a rate is shown. */
  populationPeriod: string | null;
}

// "How you compare" strip (spec §5.2). Information-only: it states the typical
// peer figure and the user's own cover status side by side, with the source
// stamped — the user does the arithmetic. No save/switch framing, no "you're
// worse off — take cover" steer (validation §3.1, §3.3). When APRA publishes
// no by-age rate we show the published count, never a modelled percentage.

/** The typical-peer line. Renders a rate when one is sourced; otherwise the
 * published count (the ABS denominator isn't in the APRA source — see the
 * dataset TODO). */
export function benchmarkTypicalLine(b: Au08BenchmarkView): string {
  if (b.coverageRate !== null) {
    return `People aged ${b.ageBand}: about ${Math.round(b.coverageRate * 100)}% hold private hospital cover.`;
  }
  return `People aged ${b.ageBand}: ${b.insuredPersons.toLocaleString("en-AU")} held private hospital cover.`;
}

/** The user's own side of the comparison — their stated cover status, no
 * judgement attached. */
export function benchmarkYouLine(b: Au08BenchmarkView): string {
  return b.holdsCover
    ? "You told us you currently hold private hospital cover."
    : "You told us you don't currently hold private hospital cover.";
}

/** Inline source attribution for the strip. When a rate is shown it's a ratio
 * of two sourced periods, so both are cited; otherwise just the count's
 * period. (`period`/`populationPeriod` already carry the "APRA …"/"ABS …"
 * prefixes.) */
export function benchmarkCaption(b: Au08BenchmarkView): string {
  if (b.coverageRate !== null && b.populationPeriod !== null) {
    return `Hospital cover by age. Cover: ${b.period}. Population: ${b.populationPeriod}.`;
  }
  return `Hospital cover by age. Source: ${b.period}.`;
}

// Hospital-cover comparison (PDF report page 5, spec §5.3). Information, not
// advice (validation §3): the page states what a single-adult policy typically
// costs per Government tier and what each tier covers, sourced from
// privatehealth.gov.au. It names no insurer, recommends no tier, and frames no
// saving — the reader sees the typical cost and decides for themselves.

/** Whole-dollar monthly premium, e.g. "A$120". The exact cents live in the
 * dataset; the page shows rounded dollars for readability. */
export function formatMonthlyAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** "A$105–A$131" — the typical (25th–75th percentile) monthly range. */
export function monthlyRange(p25: number, p75: number): string {
  return `${formatMonthlyAud(p25)}–${formatMonthlyAud(p75)}`;
}

/** Intro for the hospital-comparison page. States what the table is and the one
 * binding caveat (no recommendation), without steering toward cover. */
export function hospitalComparisonIntro(): string {
  return "Private hospital cover is sold in four Government-defined tiers — Basic, Bronze, Silver and Gold — set by the minimum clinical categories each must cover. The table below shows what a single adult's policy typically costs at each tier. These are objective figures from the Government's data; this is not a recommendation of any tier or insurer, and what's right for you depends on your own circumstances.";
}

/** What each Government tier must, as a minimum, cover. Factual descriptions of
 * the complying-product clinical-category minimums (privatehealth.gov.au) — not
 * a steer toward any tier. */
export function hospitalTierNote(tier: HospitalTier): string {
  switch (tier) {
    case "Basic":
      return "The minimum-cover tier. Most clinical categories are restricted or not covered. Some people hold a Basic policy to meet the Medicare Levy Surcharge exemption or to start Lifetime Health Cover rather than for treatment cover.";
    case "Bronze":
      return "Must cover a Government-defined minimum set of clinical categories — for example joint reconstructions, hernia and appendix, and digestive system.";
    case "Silver":
      return "Covers the Bronze categories plus further Government-defined ones — for example heart and vascular, lung and chest, and back, neck and spine.";
    case "Gold":
      return "Must cover every clinical category, including pregnancy and birth, joint replacements, and weight-loss surgery.";
  }
}

/** Source/basis caption for the page. Cites the release, the scale, the premium
 * basis, and the verified-at date — the same metadata shape the result page
 * uses elsewhere. */
export function hospitalComparisonCaption(
  period: string,
  totalPolicies: number,
  totalInsurers: number,
  verifiedAt: string,
): string {
  return `Median and typical range of the monthly premium (at the full 100% rate, before any rebate) for a single adult, across ${totalPolicies.toLocaleString("en-AU")} open hospital policies from ${totalInsurers} insurers. Source: ${period}, last verified ${verifiedAt}. Premiums vary by state, excess and insurer; check privatehealth.gov.au for policies available to you.`;
}
